import { Box, Grid, GridItem, Select, Flex } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import Show from '@/components/Molecules/Show';
import SmartInsurance from '@/components/Organisms/SmartInsurance';
import { useSaveDraft } from '@/hooks/service-hooks/drafts';
import { useGetInsurances } from '@/hooks/service-hooks/insurances';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import usePermission from '@/hooks/usePermission';
import { useAuthStore, useClinicStore } from '@/store';
import type { Insurance } from '@/types/patient';
import { DRAFT_TYPE_LABELS, FORM_TYPES } from '@/constants';
import type { PatientFormProps } from './types';

// Yup validation schema
const patientSchema = yup.object().shape({
	firstname: yup
		.string()
		.transform((value) => value?.trim())
		.required('First name is required'),
	lastname: yup
		.string()
		.transform((value) => value?.trim())
		.required('Last name is required'),
	birthdate: yup.string().required('Birth date is required'),
	gendercode: yup.string().required('Gender is required'),
	preferredcontactmethodcode: yup
		.string()
		.required('Contact method is required'),
	ta_primarymemberid: yup
		.string()
		.transform((value) => value?.trim())
		.required('Primary member ID is required'),
	clinic: yup.object<SelectItem>().required('Clinic is required'),
	primaryInsurance: yup
		.object<SelectItem>()
		.required('Primary insurance is required').nullable(),
	secondaryInsurance: yup.object<SelectItem>().nullable(),
	emailaddress1: yup
		.string()
		.transform((value) => value?.trim())
		.default('')
		.test('email', 'Invalid email format', (value) => {
			if (!value || value === '') return true;
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return emailRegex.test(value);
		}),
	ta_emrid: yup
		.string()
		.transform((value) => value?.trim())
		.optional()
		.default(''),
	mobilephone: yup
		.string()
		.transform((value) => value?.trim())
		.optional()
		.default('')
		.test('phone', 'Invalid phone number format', (value) => {
			if (!value || value === '') return true;
			// Accept formats: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
			const phoneRegex =
				/^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
			return phoneRegex.test(value);
		}),
	ta_secondarymemberid: yup
		.string()
		.transform((value) => value?.trim())
		.optional()
		.default(''),
	address1_line1: yup
		.string()
		.transform((value) => value?.trim())
		.optional()
		.default(''),
	address1_city: yup
		.string()
		.transform((value) => value?.trim())
		.optional()
		.default(''),
	address1_stateorprovince: yup
		.string()
		.transform((value) => value?.trim())
		.optional()
		.default(''),
	address1_postalcode: yup
		.string()
		.transform((value) => (typeof value === 'string' ? value.trim() : value))
		.optional()
		.default('')
		.test('postalcode', 'Postal code must contain at least 5 digits', (value) => {
			if (!value || value === '') return true;
			// Accept only numbers and dashes
			const postalCodeRegex = /^[\d-]+$/;
			if (!postalCodeRegex.test(value)) return false;
			// Count digits only (ignore dashes)
			const digitCount = (value.match(/\d/g) || []).length;
			// Require at least 5 digits for general world validation
			return digitCount >= 5;
		}),
});

type PatientFormData = yup.InferType<typeof patientSchema>;

const PatientForm: React.FC<PatientFormProps> = ({
	mode,
	initialData,
	onSubmit,
	isLoading = false,
	onIsDirtyChange,
	onExitConfirmationDraftActionChange,
	existingDraft,
	onSuccess,
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
		getValues,
		resetField,
		trigger
	} = useForm<PatientFormData>({
		resolver: yupResolver(patientSchema),
		mode: 'onChange',
		defaultValues: {},
	});

	const { hasPermission } = usePermission();
	
	const user = useAuthStore((state) => state.user?.userInformation);
	const userId = user?._id || null;
	const userName = user ? `${user.firstName} ${user.lastName}` : '';
	
	const {
		mutate: saveDraft,
		isLoading: isSavingDraftMutation,
		isEditingDraft,
	} = useSaveDraft({
		existingDraft,
		onSuccess: () => {
			toast.success(
				existingDraft ? 'Draft updated successfully' : 'Draft saved successfully',
			);
			onSuccess?.({ action: 'draft_saved' });
		},
	});

	// Load draft data
	useEffect(() => {
		if (existingDraft && mode === 'create' && !initialData) {
			try {
				reset(existingDraft.formData as PatientFormData, { 
					keepDefaultValues: false,
					keepDirty: false  // Important: Don't mark form as dirty when loading draft
				});
			} catch (error) {
				console.error('Failed to load draft data:', error);
			}
		}
	}, [existingDraft, mode, initialData, reset]);

	// Reset form when data changes (but not when loading from draft)
	useEffect(() => {
		// Don't reset if we're loading from a draft
		if (existingDraft && mode === 'create') {
			return; // Skip reset when draft is being loaded
		}
		
		if (mode === 'create') {
			if (initialData && Object.keys(initialData).length > 0) {
				reset({
					clinic: initialData.clinic,
				});
			} else {
				reset({});
			}
		} else if (initialData && Object.keys(initialData).length > 0) {
			reset(initialData);
		}
	}, [mode, initialData, reset, existingDraft]);

	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	const handleSubmitForm = handleSubmit(async (data) => {
		await onSubmit(data);
	});

	// Handle save as draft
	const handleSaveAsDraft = useCallback(() => {
		const currentFormData = getValues();
		
		// Generate title from patient name or clinic name
		const clinic = currentFormData.clinic as SelectItem | null | undefined;
		const firstname = currentFormData.firstname as string | undefined;
		const lastname = currentFormData.lastname as string | undefined;
		
		const titleOrReference = firstname && lastname 
			? `${firstname} ${lastname}`
			: clinic?.label || 'Untitled Patient Draft';
		
		saveDraft({
			userId: userId || '',
			userName,
			formType: FORM_TYPES.PATIENT,
			draftType: DRAFT_TYPE_LABELS.PATIENT,
			titleOrReference,
			formData: currentFormData as Record<string, any>,
		});
	}, [getValues, saveDraft, userId, userName]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (mode !== 'create') {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: isEditingDraft ? 'Update Draft' : 'Save as Draft',
			onClick: handleSaveAsDraft,
			loading: isSavingDraftMutation,
			disabled: isLoading || isSavingDraftMutation,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		handleSaveAsDraft,
		isEditingDraft,
		isLoading,
		isSavingDraftMutation,
		mode,
		onExitConfirmationDraftActionChange,
	]);

	// Handler to prevent multiple consecutive spaces
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const input = e.currentTarget;
		const value = input.value;
		const cursorPosition = input.selectionStart || 0;

		// Prevent multiple spaces
		if (e.key === ' ') {
			// Check if previous character is already a space
			if (cursorPosition > 0 && value[cursorPosition - 1] === ' ') {
				e.preventDefault();
			}
		}
	};

	// Handler to prevent all spaces in email field
	const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// Prevent any spaces in email
		if (e.key === ' ') {
			e.preventDefault();
		}
	};

	// Handler to allow only alphabets and spaces (for City and State/Province)
	const handleAlphabetsOnlyKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		// Allow special keys (Backspace, Delete, Arrow keys, Tab, etc.)
		const specialKeys = [
			'Backspace',
			'Delete',
			'ArrowLeft',
			'ArrowRight',
			'ArrowUp',
			'ArrowDown',
		];
		if (specialKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
			return;
		}

		const input = e.currentTarget;
		const value = input.value;
		const cursorPosition = input.selectionStart || 0;

		// Prevent multiple spaces
		if (e.key === ' ') {
			if (cursorPosition > 0 && value[cursorPosition - 1] === ' ') {
				e.preventDefault();
				return;
			}
		}

		// Allow only alphabets and spaces
		const allowedChars = /[a-zA-Z\s]/;
		if (!allowedChars.test(e.key)) {
			e.preventDefault();
		}
	};

	// Insurances with pagination (same integration as PatientInformation)
	const primaryInsurances = usePaginatedSelect({
		queryHook: useGetInsurances,
		queryParams: { enabled: true },
		transform: (insurance: Insurance) => ({
			value: insurance.ta_insuranceid,
			label: insurance.ta_name,
		}),
		errorMessage: 'Unable to load insurances',
	});
	// Insurances with pagination (same integration as PatientInformation)
	const secondaryInsurances = usePaginatedSelect({
		queryHook: useGetInsurances,
		queryParams: { enabled: true },
		transform: (insurance: Insurance) => ({
			value: insurance.ta_insuranceid,
			label: insurance.ta_name,
		}),
		errorMessage: 'Unable to load insurances',
	});

	const clinics = useClinicStore((state) => state.currentClinics).map(
		(clinic) => ({
			label: clinic.name,
			value: clinic.accountid,
		}),
	);

	// Gender options
	const genderItems: SelectItem[] = [
		{ value: '1', label: 'Male' },
		{ value: '2', label: 'Female' },
	];

	// Contact method options
	const contactMethodItems: SelectItem[] = [
		{ value: '1', label: 'Any' },
		{ value: '2', label: 'Email' },
		{ value: '3', label: 'Phone' },
		{ value: '4', label: 'Fax' },
		{ value: '5', label: 'Mail' },
	];

	return (
		<form onSubmit={handleSubmitForm}>
			<Grid
				templateColumns={{
					base: '1fr',
					md: '2fr 1fr',
				}}
				gap={4}
				px={1}
			>
				<GridItem gap={4}>
					<Grid
						templateColumns={{
							base: '1fr',
							md: '1fr 1fr',
						}}
						gap={4}
					>
						<GridItem colSpan={{ base: 1, md: 1 }}>
							<FaceliftInput
								topLabel="Patient's First Name"
								placeholder="First name"
								{...register('firstname')}
								error={errors.firstname?.message}
								onKeyDown={handleKeyDown}
								required
							/>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<FaceliftInput
								topLabel="Patient's Last Name"
								placeholder="Last name"
								{...register('lastname')}
								error={errors.lastname?.message}
								onKeyDown={handleKeyDown}
								required
							/>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<FaceliftSelect<SelectItem, false>
								topLabel="Clinic"
								placeholder="Select Clinic"
								value={watch('clinic') as SelectItem | null}
								onChange={(newValue: SingleValue<SelectItem>) => {
									setValue('clinic', newValue as SelectItem, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
									trigger('clinic');
								}}
								disabled={initialData?.clinic}
								items={clinics}
								error={errors.clinic?.message}
								isSearchable={true}
								// ai={true}
								required
							/>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<Show when={hasPermission(['SmartInsurance:Visualize'])}>
								<SmartInsurance 
									placeholder="Search..."
									toplabel="Primary Insurance"
									required
									onConfirm={(e) => {
										if (e?.plan) {
											setValue('primaryInsurance', {
												value: e.plan.id,
												label: e.plan.name,
											}, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
										} else {
											setValue('primaryInsurance', undefined as any, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
											resetField('primaryInsurance', { defaultValue: undefined });
										}
									}}
									defaultValue={{
										grouping: '',
										type: '',
										plan: {
											id: (watch('primaryInsurance') as SelectItem)?.value as string,
											name: (watch('primaryInsurance') as SelectItem)?.label 
										}
									}}
									error={errors.primaryInsurance?.message}
								/>
							</Show>
							<Show when={!hasPermission(['SmartInsurance:Visualize'])}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Primary Insurance"
									placeholder="Select Primary Insurance"
									value={watch('primaryInsurance') as SelectItem | null}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('primaryInsurance', newValue);
										}
									}}
									items={primaryInsurances.items}
									loading={primaryInsurances.loading}
									onLoadMore={primaryInsurances.onLoadMore}
									error={errors.primaryInsurance?.message}
									isSearchable={true}
									ai={true}
									aiSuggestionText={
										'Use a keyword like “United” to see more relevant results'
									}
									required
								/>
							</Show>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<FaceliftInput
								topLabel="Primary Member ID"
								placeholder="Primary member ID"
								{...register('ta_primarymemberid')}
								error={errors.ta_primarymemberid?.message}
								onKeyDown={handleKeyDown}
								required
							/>
							<Box mt={2}>
								<FaceliftInput
									topLabel="Secondary Member ID"
									placeholder="Secondary member ID"
									{...register('ta_secondarymemberid')}
									error={errors.ta_secondarymemberid?.message}
									onKeyDown={handleKeyDown}
								/>
							</Box>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<Show when={hasPermission(['SmartInsurance:Visualize'])}>
								<SmartInsurance 
									placeholder="Search..."
									toplabel="Secondary Insurance"
									onConfirm={(e) => {
										if (e?.plan) {
											setValue('secondaryInsurance', {
												value: e.plan.id,
												label: e.plan.name,
											}, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
										} else {
											setValue('secondaryInsurance', undefined as any, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
											resetField('secondaryInsurance', { defaultValue: undefined });
										}
									}}
									defaultValue={{
										grouping: '',
										type: '',
										plan: {
											id: (watch('secondaryInsurance') as SelectItem)?.value as string,
											name: (watch('secondaryInsurance') as SelectItem)?.label 
										}
									}}
								/>
							</Show>
							<Show when={!hasPermission(['SmartInsurance:Visualize'])}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Secondary Insurance"
									placeholder="Select Secondary Insurance"
									value={watch('secondaryInsurance') as SelectItem | null}
									onChange={(newValue: SingleValue<SelectItem>) => {
										if (newValue) {
											setValue('secondaryInsurance', newValue);
										}
									}}
									items={secondaryInsurances.items}
									loading={secondaryInsurances.loading}
									onLoadMore={secondaryInsurances.onLoadMore}
									error={errors.secondaryInsurance?.message}
									isSearchable={true}
									ai={true}
									aiSuggestionText={'Use a keyword like “United” to see more relevant results'}
								/>
							</Show>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 2 }}>
							<FaceliftInput
								topLabel="Street 1"
								placeholder="Street"
								{...register('address1_line1')}
								error={errors.address1_line1?.message}
								onKeyDown={handleKeyDown}
							/>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<FaceliftInput
								topLabel="City"
								placeholder="City"
								{...register('address1_city')}
								error={errors.address1_city?.message}
								onKeyDown={handleAlphabetsOnlyKeyDown}
							/>
						</GridItem>

						<GridItem colSpan={{ base: 1, md: 1 }}>
							<FaceliftInput
								topLabel="State/Province"
								placeholder="State/Province"
								{...register('address1_stateorprovince')}
								error={errors.address1_stateorprovince?.message}
								onKeyDown={handleAlphabetsOnlyKeyDown}
							/>
						</GridItem>

						{/* <GridItem colSpan={{ base: 1, md: 1 }}>
						
						</GridItem> */}
					</Grid>
				</GridItem>
				<GridItem>
					<Box display="flex" flexDirection="column" gap={4}>
						<Box>
							<FaceliftDatePicker
								topLabel="DOB"
								value={watch('birthdate')}
								onChange={(date: string) => {
									setValue('birthdate', date, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
									trigger('birthdate');
								}}
								placeholder="MM/DD/YYYY"
								error={errors.birthdate?.message}
								required
								maxDate={getTodayInNYTimezone()}
							/>
						</Box>

						<Box>
							<FaceliftSelect
								topLabel="Gender"
								placeholder="Select Gender"
								value={
									genderItems.find(
										(item) => item.value === watch('gendercode'),
									) || null
								}
								onChange={(newValue: SingleValue<SelectItem>) => {
									setValue('gendercode', (newValue?.value as string) || '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
									trigger('gendercode');
								}}
								items={genderItems}
								error={errors.gendercode?.message}
								isSearchable={false}
								required
							/>
						</Box>

						<Box>
							<FaceliftInput
								topLabel="EMR ID"
								placeholder="000-000-000"
								{...register('ta_emrid')}
								error={errors.ta_emrid?.message}
								onKeyDown={handleKeyDown}
							/>
						</Box>

						<Box mt={4}>
							<FaceliftInput
								topLabel="Mobile Phone"
								placeholder="000-000-0000"
								{...register('mobilephone')}
								error={errors.mobilephone?.message}
								onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
									const specialKeys = [
										'Backspace',
										'Delete',
										'ArrowLeft',
										'ArrowRight',
										'ArrowUp',
										'ArrowDown',
										'Tab',
									];
									if (specialKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
										return;
									}
									// Prevent multiple spaces
									const input = e.currentTarget;
									const value = input.value;
									const cursorPosition = input.selectionStart || 0;
									if (
										e.key === ' ' &&
										cursorPosition > 0 &&
										value[cursorPosition - 1] === ' '
									) {
										e.preventDefault();
										return;
									}
									// Allow only numbers and phone formatting characters
									const allowedChars = /[0-9+\-().\s]/;
									if (!allowedChars.test(e.key)) {
										e.preventDefault();
									}
								}}
							/>
						</Box>
						<Box>
							<FaceliftInput
								topLabel="Email"
								placeholder="Email"
								type="email"
								{...register('emailaddress1')}
								error={errors.emailaddress1?.message}
								onKeyDown={handleEmailKeyDown}
							/>
						</Box>

						<Box mt={4}>
							<FaceliftSelect
								topLabel="Contact Method"
								placeholder="Select Contact Method"
								value={
									contactMethodItems.find(
										(item) =>
											item.value === watch('preferredcontactmethodcode'),
									) || null
								}
								onChange={(newValue: SingleValue<SelectItem>) => {
									setValue(
										'preferredcontactmethodcode',
										(newValue?.value as string) || '',
										{ shouldDirty: true, shouldTouch: true, shouldValidate: true }
									);
									trigger('preferredcontactmethodcode');
								}}
								items={contactMethodItems}
								error={errors.preferredcontactmethodcode?.message}
								isSearchable={false}
								required
							/>
						</Box>
					<Box>
						<FaceliftInput
							topLabel="Postal Code"
							placeholder="00000"
							inputMode="numeric"
							pattern="[0-9-]*"
							maxLength={10}
							{...register('address1_postalcode')}
							onChange={(e) => {
								// Strip letters/symbols as the user types (allows digits + optional dash)
								e.target.value = e.target.value.replace(/[^\d-]/g, '');
								register('address1_postalcode').onChange(e);
							}}
							error={errors.address1_postalcode?.message}
						/>
					</Box>
					<Box
						gap={4}
						width="full"
						display="flex"
						flexDirection="column"
						justifyContent="flex-end"
						pl={{ base: 0, sm: 0, md: 14 }}
						mt={{ base: 0, sm: 4, md: 5 }}
					>
						<Show when={mode === 'create'}>
							<FaceliftButton
								width="full"
								loading={isSavingDraftMutation}
								type="button"
								onClick={handleSaveAsDraft}
								cursor="pointer"
								disabled={isLoading}
								bg="transparent"
								color="brand.600"
								border="1px solid"
								borderColor="brand.600"
								_hover={{
									bg: 'brand.50',
								}}
								_dark={{
									_hover: {
										bg: 'whiteAlpha.100',
									},
								}}
							>
								{isEditingDraft ? 'Update Draft' : 'Save as Draft'}
							</FaceliftButton>
						</Show>
						<FaceliftButton
							width="full"
							loading={isLoading}
							type="submit"
							cursor="pointer"
							disabled={isSavingDraftMutation}
						>
							Submit
						</FaceliftButton>
					</Box>
					</Box>
				</GridItem>
			</Grid>
		</form>
	);
};

export default PatientForm;
